import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUserProfile, saveUserProfile, fetchAddressByPostalCode } from '../api/userProfile';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { 
  User as UserIcon, 
  ChevronLeft, 
  MapPin, 
  Phone, 
  Calendar as CalendarIcon, 
  CheckCircle2,
  Info,
  Search,
  Loader2
} from 'lucide-react';
import classNames from 'classnames';
import MypageLayout from '../components/layouts/MypageLayout';
import { ProfileStatus } from '../components/MypageNavbar';

// ===== 年月日 =====
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
const months = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

// 根据所选年月计算当月的天数（含闰年）
const getDaysInMonth = (y?: number, m?: number) => {
  if (!y || !m) return 31;
  return new Date(y, m, 0).getDate(); // y年m月的第0日=上月最后一天
};

/** 轻量姓氏字典（常见 Top 50+，按长度降序以匹配最长前缀） */
const SURNAMES_KANJI = [
  '佐々木', '長谷川', '高橋', '山崎', '山口', '中島', '中村', '小林', '小川', '三浦', '伊藤', '吉田', '斎藤', '加藤', '山下',
  '山本', '山田', '青木', '前田', '後藤', '近藤', '石井', '石川', '松田', '森田', '橋本', '池田', '藤田', '岡本', '岡田',
  '清水', '田中', '矢野', '酒井', '横山', '西村', '木村', '村上', '松本', '阿部', '森', '太田', '福田', '原田', '渡辺',
  '遠藤', '藤井', '鈴木', '大野', '上田', '野口', '熊谷', '北村', '菊地', '今井', '柴田', '金子', '川口', '平野', '黒田'
].sort((a, b) => b.length - a.length);

/** 常见姓氏（カナ）——用于フリガナ前缀匹配，按长度降序 */
const SURNAMES_KANA = [
  'ササキ', 'ハセガワ', 'タカハシ', 'ヤマザキ', 'ヤマグチ', 'ナカジマ', 'ナカムラ', 'コバヤシ', 'オガワ', 'ミウラ',
  'イトウ', 'ヨシダ', 'サイトウ', 'カトウ', 'ヤマシタ', 'ヤマモト', 'ヤマダ', 'アオキ', 'マエダ', 'ゴトウ',
  'コンドウ', 'イシイ', 'イシカワ', 'マツダ', 'モリタ', 'ハシモト', 'イケダ', 'フジタ', 'オカモト', 'オカダ',
  'シミズ', 'タナカ', 'ヤノ', 'サカイ', 'ヨコヤマ', 'ニシムラ', 'キムラ', 'ムラカミ', 'マツモト', 'アベ',
  'モリ', 'オオタ', 'フクダ', 'ハラダ', 'ワタナベ', 'エンドウ', 'フジイ', 'スズキ', 'オオノ', 'ウエダ',
  'ノグチ', 'クマガイ', 'キタムラ', 'キクチ', 'イマイ', 'シバタ', 'カネコ', 'カワグチ', 'ヒラノ', 'クロダ'
].sort((a, b) => b.length - a.length);

// ===== ユーティリティ =====
const normalizeNFKC = (input: string): string => (input || '').normalize('NFKC').trim();

// ひらがな→カタカナ・半角→全角・长音/中点统一
const normalizeKana = (input: string): string => {
  if (!input) return '';
  let s = input.normalize('NFKC');
  s = s.replace(/[\u3041-\u3096]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) + 0x60)); // ひら→カタ
  s = s.replace(/[ｰ—―-]/g, 'ー').replace(/[･·]/g, '・'); // 长音/中点
  return s.trim();
};

// 姓名允许（汉字/かな/カナ/英字/・/ー/空白）
const nameRegex = /^[\u4E00-\u9FFF\u3040-\u30FFA-Za-z\uFF21-\uFF3A\uFF41-\uFF5A\u30FC\u30FB・\s]+$/;
// フリガナ（全角カタカナ + 英字 + ー・ + 空白）
const katakanaRegex = /^[\u30A0-\u30FFー・A-Za-z\s]+$/;
// 数字
const digitsOnly = (s: string) => s.replace(/\D/g, '');

// 「姓 名」或「姓・名」优先；否则试 Kanji 姓氏前缀；都不行时 -> 全部给「名」
const smartSplitName = (fullName: string): { last: string; first: string } => {
  const raw = fullName || '';
  const bySpace = raw.split(/\s+/).filter(Boolean);
  if (bySpace.length >= 2) return { last: bySpace[0], first: bySpace.slice(1).join(' ') };

  const byDot = raw.split('・').filter(Boolean);
  if (byDot.length >= 2) return { last: byDot[0], first: byDot.slice(1).join('・') };

  const s = normalizeNFKC(raw);
  if (!s) return { last: '', first: '' };

  const hit = SURNAMES_KANJI.find((sn) => s.startsWith(sn));
  if (hit) return { last: hit, first: s.slice(hit.length) };

  return { last: '', first: s }; // 回退：整串→名
};

// フリガナ用分割：空白/「・」优先；否则试カナ姓氏前缀；再不行→全部给「名」
const smartSplitKana = (furiRaw: string): { l: string; f: string } => {
  const k = normalizeKana(furiRaw || '');
  if (!k) return { l: '', f: '' };

  const bySpace = k.split(/\s+/).filter(Boolean);
  if (bySpace.length >= 2) return { l: bySpace[0], f: bySpace.slice(1).join(' ') };

  const byDot = k.split('・').filter(Boolean);
  if (byDot.length >= 2) return { l: byDot[0], f: byDot.slice(1).join('・') };

  const hit = SURNAMES_KANA.find(sn => k.startsWith(sn));
  if (hit) return { l: hit, f: k.slice(hit.length).trim() };

  return { l: '', f: k }; // 回退：整串→名
};

// 校验真实日期
const isValidYMD = (y?: number | string, m?: number | string, d?: number | string) => {
  const yi = Number(y), mi = Number(m), di = Number(d);
  if (!yi || !mi || !di) return false;
  const dt = new Date(yi, mi - 1, di);
  return dt.getFullYear() === yi && dt.getMonth() === mi - 1 && dt.getDate() === di;
};

const UserProfileForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<any>({
    last_name: '',
    first_name: '',
    last_name_kana: '',
    first_name_kana: '',
    gender: '',
    birth_year: '',
    birth_month: '',
    birth_day: '',
    phone1: '',
    phone2: '',
    phone3: '',
    postal_code: '',
    address: '',
  });
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [profileStatus, setProfileStatus] = useState<ProfileStatus>({ state: 'red', missing: [] });

  const computeProfileStatus = (p: any) => {
    const missing: string[] = [];
    const digits = (s?: string | null) => String(s || '').replace(/\D/g, '');
    
    const ln = (p.last_name || '').trim();
    const fn = (p.first_name || '').trim();
    const postal = digits(p.postal_code);
    const addr = (p.address || '').trim();
    const gender = (p.gender || '').trim();
    const birth = (p.birth_year && p.birth_month && p.birth_day);
    const phone = digits(`${p.phone1}${p.phone2}${p.phone3}`);

    const nameOK = ln.length > 0 && fn.length > 0;
    const postalOK = /^\d{7}$/.test(postal);
    const addrOK = addr.length > 0;
    if (!nameOK) missing.push(t('profile.full_name') || '氏名');
    if (!postalOK) missing.push(t('profile.postal_code') || '郵便番号（7桁）');
    if (!addrOK) missing.push(t('profile.address') || '住所');

    const genderOK = gender.length > 0;
    const birthOK = !!birth;
    const phoneOK = /^\d{10,13}$/.test(phone);
    if (!genderOK) missing.push(t('profile.gender') || '性別');
    if (!birthOK) missing.push(t('profile.birth_date') || '生年月日');
    if (!phoneOK) missing.push(t('profile.phone_number') || '電話番号');

    // 電話番号も「重要」に格上げ（無断キャンセル時などに患者へ連絡できるように。他画面と統一）
    const hasCriticalMissing = !nameOK || !postalOK || !addrOK || !phoneOK;
    const state = missing.length === 0 ? 'green' : hasCriticalMissing ? 'red' : 'yellow';
    setProfileStatus({ state, missing });
  };

  // ==== IME 组合输入状态（仅跟踪假名字段） ====
  const [isComposing, setIsComposing] = useState<{ [k: string]: boolean }>({
    last_name_kana: false,
    first_name_kana: false,
  });

  // 组合开始/结束
  const handleCompositionStart = (e: React.CompositionEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;
    setIsComposing((p) => ({ ...p, [name]: true }));
  };

  const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;
    const normalized = normalizeKana(e.currentTarget.value || '');
    setIsComposing((p) => ({ ...p, [name]: false }));
    setProfile((p: any) => ({ ...p, [name]: normalized }));
  };

  // 假名字段 onBlur：兜底规范 + 轻校验
  const handleKanaBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;
    if (name !== 'last_name_kana' && name !== 'first_name_kana') return;
    const v = normalizeKana(e.currentTarget.value || '');
    setProfile((p: any) => ({ ...p, [name]: v }));

    if (v && !katakanaRegex.test(v)) {
      setErrors((er: any) => ({ ...er, [name]: t('profile.invalid_kana') || 'フリガナ（カタカナ）で入力してください。' }));
    } else {
      setErrors((er: any) => {
        const { [name]: _, ...rest } = er;
        return rest;
      });
    }
  };

  // 动态日数
  const daysInSelectedMonth = useMemo(() => {
    const y = Number(profile.birth_year) || undefined;
    const m = Number(profile.birth_month) || undefined;
    return getDaysInMonth(y, m);
  }, [profile.birth_year, profile.birth_month]);

  // ==== 初期ロード ====
  useEffect(() => {
    (async () => {
      try {
        const data = await getUserProfile();
        if (data) {
          const full = String(data.full_name || '');
          const furi = normalizeKana(String(data.furigana || ''));

          // 姓名・フリガナをそれぞれスマート分割
          const { last, first } = smartSplitName(full);
          const { l: lKana, f: fKana } = smartSplitKana(furi);

          // Parse phone number: handle +81090-1234-5678 or +819012345678 or 090-1234-5678
          let phoneParts = ['', '', ''];
          if (data.phone_number) {
            let phoneStr = String(data.phone_number).trim();

            // 数字のみ抽出 → 国番号(81)除去 → 先頭0を復元。
            //   +81 は国内番号の先頭0を置換したものなので、除去後に0が無ければ戻す。
            //   例) +817012345678 / 817012345678 → 7012345678 → 07012345678
            //       +8107012345678 → 07012345678(0は既にある)
            let digits = phoneStr.replace(/\D/g, '');
            if (digits.startsWith('81')) {
              digits = digits.slice(2);
            }
            if (digits && !digits.startsWith('0')) {
              digits = '0' + digits;
            }

            // 4. Split based on length
            if (digits.length === 11) {
              // 090-1234-5678
              phoneParts = [digits.slice(0, 3), digits.slice(3, 7), digits.slice(7)];
            } else if (digits.length === 10) {
              // 03-1234-5678 or 90-1234-5678
              // Assume 3-4-3 for consistency with mobile, or 2-4-4 for landline?
              // Given the context is mostly mobile, 3-4-3 is safer or 3-3-4?
              // Let's stick to 3-4-3 (090-1234-567) or 2-4-4 (03-1234-5678)
              if (digits.startsWith('03') || digits.startsWith('06')) {
                phoneParts = [digits.slice(0, 2), digits.slice(2, 6), digits.slice(6)];
              } else {
                phoneParts = [digits.slice(0, 3), digits.slice(3, 7), digits.slice(7)];
              }
            } else if (digits.length > 0) {
              // Fallback for other lengths (e.g. truncated): fill as much as possible
              // 010123456 (9 digits) -> 010 1234 56
              const p1 = digits.slice(0, 3);
              const p2 = digits.slice(3, 7);
              const p3 = digits.slice(7);
              phoneParts = [p1, p2, p3];
            }
          }

          // 生年月日：'-' / '/' / '.' どれでも耐性を持たせる
          const birthRaw = data.birth_date ? String(data.birth_date) : '';
          const birthParts = birthRaw
            ? birthRaw.replace(/[./]/g, '-').split('-')
            : ['', '', ''];

          const p = {
            last_name: last,
            first_name: first,
            last_name_kana: lKana,
            first_name_kana: fKana,
            gender: data.gender || '',
            birth_year: birthParts[0] || '',
            birth_month: birthParts[1] || '',
            birth_day: birthParts[2] || '',
            phone1: phoneParts[0] || '',
            phone2: phoneParts[1] || '',
            phone3: phoneParts[2] || '',
            postal_code: data.postal_code ? digitsOnly(String(data.postal_code)) : '',
            address: data.address || '',
          };
          setProfile(p);
          computeProfileStatus(p);
        }
      } catch (err: any) {
        if (err?.response?.status === 404) {
          toast.info(t('profile.no_data'));
        } else {
          toast.error(t('profile.load_failed'));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  // ==== 入力変更 ====
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name } = e.target;
    let value = e.target.value;

    if (name === 'phone1' || name === 'phone2' || name === 'phone3') {
      value = digitsOnly(value);
    }
    if (name === 'postal_code') {
      value = digitsOnly(value).slice(0, 7);
    }
    if (name === 'last_name_kana' || name === 'first_name_kana') {
      value = isComposing[name] ? value : normalizeKana(value);
    }

    const next = { ...profile, [name]: value };
    setProfile(next);

    // 年/月变化时，若当前日超界，自动夹断
    if (name === 'birth_year' || name === 'birth_month') {
      const y = Number(next.birth_year);
      const m = Number(next.birth_month);
      const dim = getDaysInMonth(isNaN(y) ? undefined : y, isNaN(m) ? undefined : m);
      if (next.birth_day && Number(next.birth_day) > dim) {
        next.birth_day = String(dim).padStart(2, '0');
      }
    }
    computeProfileStatus(next);
  };

  // ==== 校验 ====
  const validate = () => {
    const newErrors: any = {};

    const ln = (profile.last_name || '').trim();
    const fn = (profile.first_name || '').trim();
    const lnk = normalizeKana(profile.last_name_kana || '');
    const fnk = normalizeKana(profile.first_name_kana || '');

    if (!ln || !nameRegex.test(ln)) newErrors.last_name = t('profile.invalid_name') || '姓を正しく入力してください。';
    if (!fn || !nameRegex.test(fn)) newErrors.first_name = t('profile.invalid_name') || '名を正しく入力してください。';

    // 假名：允许为空；有值则必须是全角カタカナ（可选项）
    if (lnk && !katakanaRegex.test(lnk)) newErrors.last_name_kana = t('profile.invalid_kana') || 'フリガナ（カタカナ）で入力してください。';
    if (fnk && !katakanaRegex.test(fnk)) newErrors.first_name_kana = t('profile.invalid_kana') || 'フリガナ（カタカナ）で入力してください。';

    if (!(profile.phone1 && profile.phone2 && profile.phone3)) {
      newErrors.phone = t('profile.invalid_phone') || '電話番号を正しく入力してください。';
    } else if (!/^\d+$/.test(profile.phone1 + profile.phone2 + profile.phone3)) {
      newErrors.phone = t('profile.invalid_phone') || '電話番号を正しく入力してください。';
    }

    if (!(profile.postal_code && /^\d{7}$/.test(profile.postal_code))) {
      newErrors.postal_code = t('profile.invalid_postal') || '郵便番号（7桁）を入力してください。';
    }

    if (/[<>]/.test(profile.address || '')) {
      newErrors.address = t('profile.invalid_address') || '住所に不正な文字が含まれています。';
    }

    // 生年月日：真实日期校验
    if (!isValidYMD(profile.birth_year, profile.birth_month, profile.birth_day)) {
      newErrors.birth = t('profile.invalid_birth') || '生年月日を選択してください。';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ==== 郵便番号から住所取得 ====
  const handlePostalCodeSearch = async () => {
    const zip = digitsOnly(profile.postal_code || '');
    if (/^\d{7}$/.test(zip)) {
      try {
        const addr = await fetchAddressByPostalCode(zip);
        setProfile((p: any) => ({ ...p, postal_code: zip, address: addr || p.address }));
        toast.success(t('profile.address_fetch_success'));
      } catch {
        toast.error(t('profile.address_fetch_failed'));
      }
    } else {
      toast.warn(t('profile.postal_code_warning') || '郵便番号は7桁の数字です。');
    }
  };

  // ==== 提交 ====
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const lnk = normalizeKana(profile.last_name_kana || '');
    const fnk = normalizeKana(profile.first_name_kana || '');
    const furi = `${lnk} ${fnk}`.trim();

    // Prepend +81 to phone number for storage (remove dashes to save space, max 14 chars in DB)
    // 標準 E.164(先頭0を除去して +81)。予約モーダルの保存形式と統一。
    const phone_number = '+81' + `${profile.phone1}${profile.phone2}${profile.phone3}`.replace(/\D/g, '').replace(/^0/, '');
    const birth_date = [
      String(profile.birth_year),
      String(profile.birth_month).padStart(2, '0'),
      String(profile.birth_day).padStart(2, '0'),
    ].join('-');

    // 关键：写回时用空格把姓名/フリガナ分开；假名空则发 null（避免后端 ResponseValidationError）
    const full_name = `${(profile.last_name || '').trim()} ${(profile.first_name || '').trim()}`.trim();
    const payload = {
      full_name,
      furigana: furi.length ? furi : null,
      gender: profile.gender || null,
      birth_date,
      phone_number,
      postal_code: digitsOnly(profile.postal_code || ''),
      address: (profile.address || '').replace(/</g, '&lt;').replace(/>/g, '&gt;'),
    };

    try {
      await saveUserProfile(payload);
      toast.success(t('profile.save_success'));
      navigate('/mypage', { state: { refresh: true } });
    } catch {
      toast.error(t('profile.save_failed'));
    }
  };

  if (loading) return (
    <div className="flex-1 w-full bg-slate-50 min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-coral" />
    </div>
  );

  // 根据所选年月，动态渲染 1..daysInSelectedMonth
  const dayOptions = Array.from({ length: daysInSelectedMonth }, (_, i) => String(i + 1).padStart(2, '0'));

  return (
    <MypageLayout
      title={t('profile.title') || 'プロフィール設定'}
      profileStatus={profileStatus}
      hideNavbar={true}
      customHeader={
        <div className="relative flex items-center justify-center py-5 sm:py-6 border-b border-slate-100 bg-slate-50/50 rounded-t-[2rem] sm:rounded-t-[2.5rem]">
          <button 
            type="button"
            onClick={() => navigate('/mypage')} 
            className="absolute left-6 group flex items-center gap-1.5 text-slate-400 hover:text-coral transition-all font-bold text-sm"
          >
            <div className="w-8 h-8 rounded-full bg-white shadow-sm border border-slate-100 flex items-center justify-center group-hover:border-coral/20 group-hover:bg-coral/5 transition-all">
              <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
            </div>
            <span className="hidden sm:inline">{t('profile.back') || '戻る'}</span>
          </button>
          <h1 className="text-base sm:text-lg font-black text-navy tracking-tight">
             {t('profile.title') || 'プロフィール設定'}
          </h1>
        </div>
      }
    >
      <div className="flex-1 w-full max-w-2xl mx-auto pt-4 sm:pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section 1: Basic Identity */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="bg-slate-50/50 px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <UserIcon className="w-5 h-5 text-navy" />
              <h3 className="font-black text-navy">{t('profile.identity_section') || 'ご本人確認'}</h3>
            </div>
            
            <div className="p-6 space-y-6">
              {/* 氏名 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-black text-slate-500 ml-1">{t('profile.last_name') || '姓'}</label>
                  <input
                    name="last_name"
                    value={profile.last_name}
                    onChange={handleChange}
                    placeholder={t('profile.placeholder_last') || '例: 山田'}
                    className="w-full bg-slate-50 border-transparent focus:bg-white focus:ring-2 focus:ring-navy/5 focus:border-navy/20 rounded-2xl px-4 py-3 text-sm transition-all"
                  />
                  {errors.last_name && <p className="text-red-500 text-[10px] font-bold ml-1">{errors.last_name}</p>}
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-black text-slate-500 ml-1">{t('profile.first_name') || '名'}</label>
                  <input
                    name="first_name"
                    value={profile.first_name}
                    onChange={handleChange}
                    placeholder={t('profile.placeholder_first') || '例: 太郎'}
                    className="w-full bg-slate-50 border-transparent focus:bg-white focus:ring-2 focus:ring-navy/5 focus:border-navy/20 rounded-2xl px-4 py-3 text-sm transition-all"
                  />
                  {errors.first_name && <p className="text-red-500 text-[10px] font-bold ml-1">{errors.first_name}</p>}
                </div>
              </div>

              {/* 假名 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between px-1">
                    <label className="text-xs font-black text-slate-500">{t('profile.last_name_kana') || '姓（フリガナ）'}</label>
                    <span className="text-[9px] font-bold text-slate-400">{t('profile.optional') || '任意'}</span>
                  </div>
                  <input
                    name="last_name_kana"
                    value={profile.last_name_kana}
                    onChange={handleChange}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                    onBlur={handleKanaBlur}
                    placeholder={t('profile.placeholder_last_kana') || '例: ヤマダ'}
                    className="w-full bg-slate-50 border-transparent focus:bg-white focus:ring-2 focus:ring-navy/5 focus:border-navy/20 rounded-2xl px-4 py-3 text-sm transition-all"
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between px-1">
                    <label className="text-xs font-black text-slate-500">{t('profile.first_name_kana') || '名（フリガナ）'}</label>
                    <span className="text-[9px] font-bold text-slate-400">{t('profile.optional') || '任意'}</span>
                  </div>
                  <input
                    name="first_name_kana"
                    value={profile.first_name_kana}
                    onChange={handleChange}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                    onBlur={handleKanaBlur}
                    placeholder={t('profile.placeholder_first_kana') || '例: タロウ'}
                    className="w-full bg-slate-50 border-transparent focus:bg-white focus:ring-2 focus:ring-navy/5 focus:border-navy/20 rounded-2xl px-4 py-3 text-sm transition-all"
                  />
                </div>
              </div>

              {/* 性別 & 生年月日 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-black text-slate-500 ml-1">{t('profile.gender') || '性別'}</label>
                  <div className="flex p-1 bg-slate-100 rounded-2xl">
                    {['男性', '女性'].map((g) => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setProfile((p: any) => ({ ...p, gender: g }))}
                        className={classNames(
                          "flex-1 py-2 text-sm font-black rounded-xl transition-all",
                          profile.gender === g ? "bg-white text-navy shadow-sm" : "text-slate-400 hover:text-slate-600"
                        )}
                      >
                        {g === '男性' ? (t('profile.male') || '男性') : (t('profile.female') || '女性')}
                      </button>
                    ))}
                  </div>
                  {errors.gender && <p className="text-red-500 text-[10px] font-bold ml-1">{errors.gender}</p>}
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-black text-slate-500 ml-1">{t('profile.birth_date') || '生年月日'}</label>
                  <div className="grid grid-cols-3 gap-2">
                    <select
                      name="birth_year"
                      value={profile.birth_year}
                      onChange={handleChange}
                      className="bg-slate-50 border-transparent rounded-xl px-2 py-2 text-sm font-bold focus:ring-0"
                    >
                      <option value="">{t('profile.year_unit') || '年'}</option>
                      {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <select
                      name="birth_month"
                      value={profile.birth_month}
                      onChange={handleChange}
                      className="bg-slate-50 border-transparent rounded-xl px-2 py-2 text-sm font-bold focus:ring-0"
                    >
                      <option value="">{t('profile.month_unit') || '月'}</option>
                      {months.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <select
                      name="birth_day"
                      value={profile.birth_day}
                      onChange={handleChange}
                      className="bg-slate-50 border-transparent rounded-xl px-2 py-2 text-sm font-bold focus:ring-0"
                    >
                      <option value="">{t('profile.day_unit') || '日'}</option>
                      {dayOptions.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  {errors.birth && <p className="text-red-500 text-[10px] font-bold ml-1">{errors.birth}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Contact Info */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="bg-slate-50/50 px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <Phone className="w-5 h-5 text-navy" />
              <h3 className="font-black text-navy">{t('profile.contact_section') || '連絡先情報'}</h3>
            </div>
            <div className="p-6">
              <div className="space-y-1.5">
                <label className="text-xs font-black text-slate-500 ml-1">{t('profile.phone_number') || '電話番号'}</label>
                <div className="flex gap-1.5 items-center">
                  <input
                    name="phone1"
                    value={profile.phone1}
                    onChange={handleChange}
                    placeholder={t('profile.phone_placeholder1') || '090'}
                    className="w-16 min-w-0 bg-slate-50 border-transparent rounded-2xl px-2 py-3 text-sm text-center font-bold focus:ring-navy/5"
                    inputMode="numeric"
                    maxLength={3}
                  />
                  <span className="text-slate-300 shrink-0">-</span>
                  <input
                    name="phone2"
                    value={profile.phone2}
                    onChange={handleChange}
                    placeholder={t('profile.phone_placeholder2') || '1234'}
                    className="flex-1 min-w-0 bg-slate-50 border-transparent rounded-2xl px-2 py-3 text-sm text-center font-bold focus:ring-navy/5"
                    inputMode="numeric"
                    maxLength={4}
                  />
                  <span className="text-slate-300 shrink-0">-</span>
                  <input
                    name="phone3"
                    value={profile.phone3}
                    onChange={handleChange}
                    placeholder={t('profile.phone_placeholder3') || '5678'}
                    className="flex-1 min-w-0 bg-slate-50 border-transparent rounded-2xl px-2 py-3 text-sm text-center font-bold focus:ring-navy/5"
                    inputMode="numeric"
                    maxLength={4}
                  />
                </div>
                {errors.phone && <p className="text-red-500 text-[10px] font-bold ml-1">{errors.phone}</p>}
              </div>
            </div>
          </div>

          {/* Section 3: Shipping / Address */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="bg-slate-50/50 px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-navy" />
              <h3 className="font-black text-navy">{t('profile.address_section') || 'お届け先・住所'}</h3>
            </div>
            <div className="p-6 space-y-6">
              <div className="space-y-1.5">
                <label className="text-xs font-black text-slate-500 ml-1">{t('profile.postal_code') || '郵便番号'}</label>
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <input
                      name="postal_code"
                      value={profile.postal_code}
                      onChange={handleChange}
                      placeholder={t('profile.zip_placeholder') || '1234567'}
                      maxLength={7}
                      className="w-full bg-slate-50 border-transparent focus:bg-white focus:ring-2 focus:ring-navy/5 focus:border-navy/20 rounded-2xl pl-10 pr-4 py-3 text-sm transition-all"
                      inputMode="numeric"
                    />
                    <Info className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                  </div>
                  <button
                    type="button"
                    onClick={handlePostalCodeSearch}
                    className="px-6 py-3 bg-navy text-white rounded-2xl shadow-md hover:bg-navy/90 transition-all font-bold text-sm flex items-center gap-2"
                  >
                    <Search className="w-4 h-4" />
                    {t('profile.search') || '住所検索'}
                  </button>
                </div>
                {errors.postal_code && <p className="text-red-500 text-[10px] font-bold ml-1">{errors.postal_code}</p>}
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-black text-slate-500 ml-1">{t('profile.address') || '住所'}</label>
                <input
                  name="address"
                  value={profile.address}
                  onChange={handleChange}
                  placeholder={t('profile.address_placeholder') || '例: 東京都港区... (マンション名・部屋番号まで入力)'}
                  className="w-full bg-slate-50 border-transparent focus:bg-white focus:ring-2 focus:ring-navy/5 focus:border-navy/20 rounded-2xl px-4 py-3 text-sm transition-all font-bold"
                />
                <p className="text-[10px] text-slate-400 font-bold ml-1">
                  {t('profile.address_hint') || '※お薬を正しくお届けするため、必ず建物名と部屋番号までご入力ください。'}
                </p>
                {errors.address && <p className="text-red-500 text-[10px] font-bold ml-1">{errors.address}</p>}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="w-full bg-gradient-to-r from-coral to-rose-500 text-white py-4 rounded-2xl shadow-lg shadow-coral/20 font-black text-base flex items-center justify-center gap-2 hover:opacity-90 transition-all"
            >
              <CheckCircle2 className="w-5 h-5" />
              {(profile.last_name || profile.first_name)
                ? (t('profile.update_button') || '設定を更新する')
                : (t('profile.save_button') || '設定を保存する')}
            </motion.button>
            <p className="text-center text-[11px] text-slate-400 font-bold mt-4">
              {t('profile.privacy_notice') || 'ご入力いただいた情報は、診療およびお薬の配送にのみ使用されます。'}
            </p>
          </div>
        </form>
      </div>
    </MypageLayout>
  );
};

export default UserProfileForm;
